import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { runtime } from '../utils/runtime';
import { fetchWordPressLocationsForBbox, normalizeLocationRequestBbox, REAL_LOCATIONS_API_PATH } from '../game-map/realLocations.mjs';
import { readLocationCacheRecords, writeLocationCacheRecord } from './locationCache.mjs';
import {
  bboxContains,
  bboxCenter,
  cacheAgeState,
  createRequestVersionGate,
  mergeRevalidatedLocations,
  readLastViewport,
  shouldRevalidateCoverage,
  writeLastViewport,
} from './locationStoreCore.mjs';

const LocationStoreContext = createContext(null);
const ENDPOINT = runtime?.locationsApiUrl || REAL_LOCATIONS_API_PATH;
const VIEWPORT_DEBOUNCE_MS = 180;
const LOCATION_NETWORK_TIMEOUT_MS = 12000;

function initialStatus() {
  return {
    loading: false,
    source: 'none',
    error: null,
    updatedAt: 0,
    coverageBbox: null,
    cacheAgeMs: null,
    stale: false,
    veryStale: false,
    requestCount: 0,
    cacheHits: 0,
    cacheMisses: 0,
    transferredBytes: 0,
    lastRequestMs: null,
  };
}

export function LocationStoreProvider({ children }) {
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState(initialStatus);
  const locationsRef = useRef([]);
  const statusRef = useRef(status);
  const activeRequestRef = useRef(null);
  const gateRef = useRef(createRequestVersionGate());
  const debounceRef = useRef(null);
  const lastViewportRef = useRef(readLastViewport());

  useEffect(() => { locationsRef.current = locations; }, [locations]);
  useEffect(() => { statusRef.current = status; }, [status]);

  const publishCacheRecords = useCallback((records, requestedBbox) => {
    if (!records.length) return null;
    const newest = records[0];
    const merged = [...records].reverse().reduce((all, record) => mergeRevalidatedLocations(all, record.locations, record.bbox), locationsRef.current);
    locationsRef.current = merged;
    setLocations(merged);
    const age = cacheAgeState(newest.updatedAt);
    setStatus((previous) => ({
      ...previous,
      source: 'cache',
      error: null,
      updatedAt: newest.updatedAt,
      coverageBbox: newest.bbox,
      cacheAgeMs: age.ageMs,
      stale: age.stale,
      veryStale: age.veryStale,
      cacheHits: previous.cacheHits + 1,
    }));
    return records.find((record) => bboxContains(record.bbox, requestedBbox)) || newest;
  }, []);

  const hydrate = useCallback(async (bbox) => {
    const records = await readLocationCacheRecords(bbox);
    if (!records.length) {
      setStatus((previous) => ({ ...previous, cacheMisses: previous.cacheMisses + 1 }));
      return null;
    }
    return publishCacheRecords(records, bbox);
  }, [publishCacheRecords]);

  const executeNetworkRequest = useCallback(async (bbox, cachedRecord, force = false) => {
    if (!force && activeRequestRef.current?.key === bbox.key) return activeRequestRef.current.promise;
    if (activeRequestRef.current?.controller) activeRequestRef.current.controller.abort();

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort('location-timeout'), LOCATION_NETWORK_TIMEOUT_MS);
    const requestVersion = gateRef.current.next();
    const startedAt = performance?.now?.() ?? Date.now();
    setStatus((previous) => ({ ...previous, loading: true, error: null, requestCount: previous.requestCount + 1 }));

    const promise = fetchWordPressLocationsForBbox({
      bbox,
      endpoint: ENDPOINT,
      signal: controller.signal,
      ifNoneMatch: cachedRecord?.etag || '',
      cachedLocations: cachedRecord?.locations || [],
    }).then(async (result) => {
      if (!gateRef.current.isCurrent(requestVersion)) return locationsRef.current;
      const now = Date.now();
      const nextLocations = mergeRevalidatedLocations(locationsRef.current, result.locations, bbox);
      locationsRef.current = nextLocations;
      setLocations(nextLocations);
      const record = { bbox, locations: result.locations, updatedAt: now, etag: result.etag || cachedRecord?.etag || '' };
      await writeLocationCacheRecord(record);
      const elapsed = (performance?.now?.() ?? Date.now()) - startedAt;
      setStatus((previous) => ({
        ...previous,
        loading: false,
        source: result.notModified ? 'cache-validated' : 'network',
        error: null,
        updatedAt: now,
        coverageBbox: bbox,
        cacheAgeMs: 0,
        stale: false,
        veryStale: false,
        transferredBytes: previous.transferredBytes + Number(result.transferredBytes || 0),
        lastRequestMs: Math.round(elapsed),
      }));
      return nextLocations;
    }).catch((error) => {
      if (!gateRef.current.isCurrent(requestVersion)) return locationsRef.current;
      const timedOut = controller.signal.reason === 'location-timeout';
      setStatus((previous) => ({
        ...previous,
        loading: false,
        source: locationsRef.current.length ? 'cache' : 'none',
        error: locationsRef.current.length
          ? 'Die Ortsdaten konnten im Hintergrund nicht aktualisiert werden. Der letzte bekannte Stand bleibt sichtbar.'
          : timedOut ? 'Die Pfand-Orte laden gerade zu langsam. Bitte versuche es gleich erneut.' : error?.message || 'Pfand-Orte konnten nicht geladen werden.',
      }));
      return locationsRef.current;
    }).finally(() => {
      window.clearTimeout(timeoutId);
      if (activeRequestRef.current?.version === requestVersion) activeRequestRef.current = null;
    });
    activeRequestRef.current = { key: bbox.key, controller, version: requestVersion, promise };
    return promise;
  }, []);

  const requestViewport = useCallback(async (rawBbox, { force = false, immediate = false } = {}) => {
    const bbox = rawBbox?.key ? rawBbox : normalizeLocationRequestBbox(rawBbox);
    if (!bbox) return locationsRef.current;
    writeLastViewport(bbox, rawBbox?.zoom);
    lastViewportRef.current = readLastViewport();

    const cachedRecord = await hydrate(bbox);
    const currentStatus = statusRef.current;
    const needsNetwork = shouldRevalidateCoverage({
      requestedBbox: bbox,
      coverageBbox: cachedRecord?.bbox || currentStatus.coverageBbox,
      updatedAt: cachedRecord?.updatedAt || currentStatus.updatedAt,
      lastNetworkCenter: bboxCenter(cachedRecord?.bbox || currentStatus.coverageBbox),
      force,
    });
    if (!needsNetwork) return locationsRef.current;

    if (immediate || force) return executeNetworkRequest(bbox, cachedRecord, force);
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current.timer);
      debounceRef.current.resolve(locationsRef.current);
    }
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        debounceRef.current = null;
        executeNetworkRequest(bbox, cachedRecord, false).then(resolve);
      }, VIEWPORT_DEBOUNCE_MS);
      debounceRef.current = { timer, resolve };
    });
  }, [executeNetworkRequest, hydrate]);

  useEffect(() => {
    const last = lastViewportRef.current;
    if (last?.bbox) void requestViewport(last.bbox, { immediate: true });
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current.timer);
        debounceRef.current.resolve(locationsRef.current);
      }
      activeRequestRef.current?.controller?.abort();
    };
  }, [requestViewport]);

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      const last = lastViewportRef.current;
      if (!last?.bbox) return;
      const age = cacheAgeState(statusRef.current.updatedAt);
      if (age.stale) void requestViewport(last.bbox, { immediate: true });
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('pageshow', resume);
      window.removeEventListener('focus', resume);
    };
  }, [requestViewport]);

  const value = useMemo(() => ({
    locations,
    ...status,
    lastViewport: lastViewportRef.current,
    requestViewport,
    refresh: (bbox) => requestViewport(bbox || lastViewportRef.current?.bbox, { force: true, immediate: true }),
  }), [locations, requestViewport, status]);

  return <LocationStoreContext.Provider value={value}>{children}</LocationStoreContext.Provider>;
}

export function useLocationStore() {
  const value = useContext(LocationStoreContext);
  if (!value) throw new Error('useLocationStore benötigt den LocationStoreProvider.');
  return value;
}
